import { CustomListKey, IPlacement } from "@sp-crm/core";
import { useTaskBadge } from "components/tasks/child";
import { Alert } from "components/ui/dialog/alert";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Location } from "history";
import { useTaskNouns } from "hooks/use-task-nouns";
import React, { useCallback, useEffect } from "react";
import { useHistory } from "react-router";
import { useCustomList } from "store/selectors/bridge";
import { useFeature } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";

interface NoActiveTasksPromptProps {
    placement: IPlacement;
}

export const NoActiveTasksPrompt: React.FC<NoActiveTasksPromptProps> = props => {
    const { placement } = props;
    const nouns = useTaskNouns();
    const { activeCount } = useTaskBadge(placement.id);
    const detailsPageVNext = useFeature("detailsPageVNext");
    const todoDestination = detailsPageVNext
        ? `/clients/show/${placement.id}/overview`
        : `/clients/show/${placement.id}/tasks`;

    const history = useHistory();
    const [showAlert, setShowAlert] = React.useState(false);
    const { settings } = useTenantSettings();
    const { itemHasCategory } = useCustomList(CustomListKey.ClientStatus);

    const handleDismiss = useCallback(() => {
        setShowAlert(false);
    }, [setShowAlert]);

    const shouldShow = useCallback(
        (targetLocation: Location | null) => {
            if (!settings.alertClientsWithNoTasks) {
                return false;
            }

            if (
                itemHasCategory(placement.statusListItemId, "Closed") ||
                placement.archived
            ) {
                return false;
            }

            if (targetLocation?.pathname?.includes(`/clients/show/${placement.id}`)) {
                return false;
            }

            if (
                targetLocation?.pathname?.match(
                    /^\/files\/[^/]+\/signatures\/[^/]+\/new$/,
                )
            ) {
                return false;
            }

            if (!activeCount) {
                return true;
            }

            return false;
        },
        [
            activeCount,
            placement.id,
            placement.statusListItemId,
            itemHasCategory,
            settings.alertClientsWithNoTasks,
            placement.archived,
        ],
    );

    useEffect(() => {
        const unblock = history.block((targetLocation: Location) => {
            if (shouldShow(targetLocation)) {
                setShowAlert(true);
                return false;
            }

            return undefined;
        });

        return () => {
            unblock();
        };
    }, [shouldShow]); // eslint-disable-line react-hooks/exhaustive-deps

    return showAlert ? (
        <Alert
            title={`No active ${nouns.plural}`}
            onDismiss={handleDismiss}
            buttons={({ close }) => {
                return (
                    <div className="flex items-center space-x-2">
                        <PrimaryButton
                            onClick={e => {
                                e.preventDefault();
                                history.push(todoDestination);
                                close();
                            }}>
                            {`Go to ${nouns.plural}`}
                        </PrimaryButton>
                        <SecondaryButton
                            onClick={e => {
                                e.preventDefault();
                                close();
                            }}>
                            Close
                        </SecondaryButton>
                    </div>
                );
            }}>
            <p>
                {`This client has no active ${nouns.plural}. Make sure to add a ${nouns.singular} or mark this client as closed.`}
            </p>
        </Alert>
    ) : (
        <></>
    );
};
